import { DEG, bump, clamp01, lerp, smoothstep } from '../core/math'

/**
 * Terrain sculpting primitives, shared by the hand-authored slope builder and
 * the procedural generator (which is now an authoring aid rather than a runtime
 * path).
 *
 * Every rule the hand-built Phase 1 face and the Phase 2 generator learned the
 * hard way lives in here, once:
 *
 *   - couloir walls run out far enough to land near 55°, not 70°; past that they
 *     scour to rock and stop being a line you can ride
 *   - a cliff band's weakness has to track the couloir feeding it, because the
 *     couloir drifts as it descends and a gate at a fixed x lands on its wall
 *   - every fade-out is length-checked against the base slope, because a
 *     depression closing faster than the ground falls away is an uphill ramp
 */

/**
 * Minimum downhill distance a feature of this depth may fade out over, so the
 * fill-in rate stays comfortably below the ground's own gradient.
 */
export function safeFade(depth: number, baseAngleDeg: number): number {
  const usable = Math.max(6, baseAngleDeg - 12)
  return Math.max(70, depth / Math.tan(usable * DEG))
}

/**
 * Flat-floored slot with steep-but-rideable walls.
 * `d` is lateral distance from the centreline in half-widths.
 */
export function couloirSlot(d: number): number {
  return 1 - smoothstep(0.45, 1.9, d)
}

/** The raised lip just outside a couloir, which is what makes it read from above. */
export function couloirWall(d: number): number {
  return bump((d - 2.1) / 1.0)
}

/**
 * A cliff band's downhill profile at a given x.
 * `weakness` 0 = full drop over 7m (a cliff), 1 = a third of it over 36m (a ramp).
 */
export function cliffStep(z: number, bandZ: number, drop: number, weakness: number): number {
  const w = clamp01(weakness)
  const d = lerp(drop, drop * 0.3, w)
  const width = lerp(7, 36, w)
  return d * smoothstep(bandZ - width * 0.5, bandZ + width * 0.5, z)
}

/**
 * Rounded ribs across a spine wall. `local` is offset from the wall centre in
 * spans. Amplitude should stay near 0.2x the rib wavelength — 8m of relief over
 * a 40m wavelength is a spine you can ride the crest of, 13m is a fin you fall
 * off.
 */
export function spineRibs(local: number, ribCount: number): number {
  if (Math.abs(local) >= 1.25) return 0
  const ribs = Math.cos(local * Math.PI * ribCount)
  const shaped = Math.pow(clamp01(0.5 * (ribs + 1)), 0.62)
  return bump(local / 1.25) * (shaped - 0.42)
}

/** Smooth on/off envelope down the fall line, with a safe fade-out. */
export function envelope(z: number, z0: number, z1: number, fadeIn: number, fadeOut: number): number {
  return smoothstep(z0, z0 + fadeIn, z) * (1 - smoothstep(z1, z1 + fadeOut, z))
}
